import type { BranchSettingsView } from "@planerius/contracts/cabinet"
import { type ReactNode, useCallback, useEffect, useState } from "react"

import { type ApiProblem, isApiProblem } from "../../api/problem"
import { fetchBranches } from "../../api/settings.api"
import { ProblemScreen, Skeleton } from "../../shell/states"
import { Badge, Button } from "../../ui"
import { StandHeader } from "../directories/stand"
import { PACKING_TEXT } from "./branch-grid-model"
import styles from "./branch-screen.module.css"

/**
 * Список филиалов — вход в 4-Б1.
 *
 * ПОЧЕМУ СПИСОК ВООБЩЕ НУЖЕН. Раздел «Филиалы» был заглушкой, и попасть к настройкам
 * сетки было неоткуда: единственный способ сменить шаг — запрос к API. Список короткий
 * нарочно: он не пытается быть карточкой филиала, а отвечает на один вопрос — «какой
 * филиал настраиваем».
 *
 * У КАЖДОЙ СТРОКИ НАЗВАНЫ ШАГ И РЕЖИМ. Владелец сети смотрит сюда затем, чтобы увидеть
 * расхождение: на Ленина 15 минут, на Гагарина 30, и это либо решение, либо забытая
 * настройка. Из одинаковых строк «Филиал →» этого не видно.
 */
export function BranchesScreen(props: {
  readonly onOpen: (branchId: string) => void
}): ReactNode {
  const [branches, setBranches] = useState<readonly BranchSettingsView[] | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const all = await fetchBranches(signal)
      if (signal?.aborted === true) return
      setBranches(all)
      setProblem(null)
    } catch (error) {
      if (signal?.aborted === true) return
      if (!isApiProblem(error)) throw error
      setProblem(error)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load])

  if (problem !== null && branches === null) {
    return <ProblemScreen problem={problem} onRetry={() => void load()} />
  }

  return (
    <div className={styles["screen"]}>
      <StandHeader title="Филиалы" repeatsSection />

      {branches === null ? (
        <div className={styles["skeletons"]}>
          {[0, 1].map((row) => (
            <Skeleton key={row} width="100%" height={56} radius={10} />
          ))}
        </div>
      ) : (
        <div className={styles["column"]}>
          {branches.length === 0 ? (
            <p className={styles["hint"]}>
              Филиалов пока нет. Первый заводится вместе с салоном — если его нет, напишите
              тому, кто подключал Planerius.
            </p>
          ) : (
            branches.map((branch) => (
              <div key={branch.id} className={styles["block"]}>
                <div className={styles["blockTitle"]}>
                  {branch.name}
                  {branch.isActive ? null : <Badge tone="gray">выключен</Badge>}
                </div>
                <p className={styles["hint"]}>
                  Шаг {branch.slotStepMinutes} мин · {PACKING_TEXT[branch.packingMode].title} ·{" "}
                  {branch.timezone}
                  {branch.address === null ? "" : ` · ${branch.address}`}
                </p>
                <div className={styles["chips"]}>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      props.onOpen(branch.id)
                    }}
                  >
                    Шаг сетки и режим дня
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
